import { useMemo, useState } from 'react';
import { Box, ButtonBase, makeStyles } from '@material-ui/core';
import _ from 'lodash';
import { AutoSizer, CellMeasurer, CellMeasurerCache, Column, Table } from 'react-virtualized';
import moment from 'moment';
import { classNames, conditional, getUserInfo } from '../../../../../utils';
import { i18n } from '../../../../../language';
import { Alert } from '../../../../../components';
import OtherInfo from '../../../components/BusStop/components/OtherInfo';
import useIpad from '../../../../../utils/useIpad';
import defaultHeaderRenderer from '../../../components/BusStop/components/headerRenderer';
import { useAdmissionVersionError } from '../../../EmergencyAdmission/EAdmissionForm';
import { BarcodeIcon, HiddenBarcodeIcon } from '../../../../../components/Icon';
import Row from './Row';

const TicketTable = ({
  data,
  openEdit,
  handleAck,
  setSort,
  sort,
  ticketType,
  setRefreshTicketList,
  isHistory,
  isBsMode,
}) => {
  const classes = useStyles();
  const userInfo = getUserInfo();
  const [successAlert, setSuccessAlert] = useState(false);
  const isIpad = useIpad();
  const handleAdmissionVersionError = useAdmissionVersionError();
  const [showBarcode, setShowBarcode] = useState(false);

  let columns = [
    {
      label: 'A.D.T',
      dataKey: 'adt',
      widthBasis: 0.07,
    },
    {
      label: 'WT.',
      dataKey: 'adt',
      widthBasis: 0.05,
    },
    {
      label: 'Specialty',
      dataKey: 'adt',
      widthBasis: 0.08,
    },
    {
      // label: 'Patient',
      dataKey: 'adt',
      widthBasis: 0.1796,
      headerRenderer: () => (
        <Box display="flex" alignItems="center">
          <Box mr="0.357rem">Patient</Box>
          {(!isBsMode || (isBsMode && isHistory)) && (
            <ButtonBase className={classes.headerActionButton} onClick={() => setShowBarcode(!showBarcode)}>
              {showBarcode ? (
                <HiddenBarcodeIcon className={classes.headerActionIcon} />
              ) : (
                <BarcodeIcon className={classes.headerActionIcon} />
              )}
            </ButtonBase>
          )}
        </Box>
      ),
    },
    {
      label: 'Diagnosis',
      dataKey: 'adt',
      widthBasis: isIpad ? 0.08 : 0.1303,
    },
    {
      label: 'Priority',
      dataKey: 'adt',
      widthBasis: 0.065,
    },
    {
      label: 'Ward',
      dataKey: 'adt',
      widthBasis: 0.11,
    },
    {
      label: 'Current Status',
      dataKey: 'adt',
      widthBasis: 0.1,
    },
    {
      label: 'Waiting For',
      dataKey: 'adt',
      widthBasis: 0.09,
    },
    {
      label: 'Remark',
      dataKey: 'adt',
      widthBasis: isIpad ? 0.08 : 0.11,
    },
    {
      label: '',
      dataKey: 'adt',
      widthBasis: isIpad ? 0.06 : 0.04,
    },
    {
      label: '',
      dataKey: 'adt',
      widthBasis: 0.06,
    },
  ];

  if (isHistory) {
    columns.splice(10, 0, {
      label: 'Ack. Date, By, Type',
      dataKey: 'adt',
      widthBasis: 0.1,
    });
    columns.splice(12, 1);
  }

  const handleSort = (type) => () => {
    setSort((preSort) => {
      if (preSort.sortKey === type) {
        return {
          sortKey: type,
          sortOrder: preSort.sortOrder === 'asc' ? 'desc' : 'asc',
        };
      } else {
        return {
          sortKey: type,
          sortOrder: 'asc',
        };
      }
    });
  };

  const tableData = useMemo(() => {
    let tempData = _.map(
      _.filter(data, (item) => item.display),
      (item) => ({
        ...item,
        patient: {
          caseNo: item.caseNo,
          bedNo: item.owBedNo,
          chiName: item.chiName,
          engName: item.engName,
          gender: item.sex,
          age: conditional(item.dob, moment().diff(moment(item.dob, 'DD-MM-YYYY'), 'years'), ''),
        },
        hold: item?.status === 'Hold',
      })
    );

    return tempData;
  }, [data, userInfo]);
  console.log('tableData', tableData);

  // 创建一个 CellMeasurerCache，用于缓存测量结果
  const cache = new CellMeasurerCache({
    defaultHeight: 48, // 默认高度
    fixedWidth: true, // 如果表格的宽度是固定的，可以设置为 true
  });

  const cellRenderer = ({ columnIndex, rowIndex, key, parent }) => {
    const rowData = tableData[rowIndex];
    const cellKey = `${rowIndex}-${columnIndex}`;
    const otherInfo = rowData.otherInfo;

    if (!cache.has(rowIndex, columnIndex)) {
      cache.clearAll(); // 清除缓存以避免其他测量结果干扰
      cache.has(rowIndex, columnIndex); // 手动执行测量
    }

    return (
      <CellMeasurer
        cache={cache}
        columnIndex={columnIndex}
        key={`${rowData?.eaId}-${rowIndex}-${columnIndex}-${otherInfo ? 'isOther' : 'notOther'}`}
        parent={parent}
        rowIndex={rowIndex}
      >
        {({ measure }) => {
          if (otherInfo) {
            if (columnIndex === 0) {
              return (
                <div key={cellKey} onLoad={measure}>
                  <OtherInfo rowData={rowData} />
                </div>
              );
            }
            return null;
          }

          return (
            <Box key={cellKey} onLoad={measure} p="0.5rem 0.5rem 0.5rem 0">
              <Row
                {...rowData}
                columnIndex={columnIndex}
                openEdit={openEdit}
                setSuccessAlert={setSuccessAlert}
                handleAck={handleAck}
                measure={measure}
                ticketType={ticketType}
                setRefreshTicketList={setRefreshTicketList}
                isHistory={isHistory}
                handleAdmissionVersionError={handleAdmissionVersionError}
                showBarcode={showBarcode}
              />
            </Box>
          );
        }}
      </CellMeasurer>
    );
  };

  const getRowClassName = ({ index }) => {
    const rowData = tableData[index];
    if (rowData?.busStopRow) {
      return '';
    }

    const { waitTime, otherInfo } = rowData || {};
    const baseClass = otherInfo ? classes.otherInfo : classes.flexContainer;

    if (rowData?.busStopRow === false) {
      if (waitTime <= 14400) {
        return classNames(baseClass, classes.borderRow1);
      } else if (waitTime <= 28800) {
        return classNames(baseClass, classes.borderRow2);
      } else if (waitTime <= 43200) {
        return classNames(baseClass, classes.borderRow3);
      } else {
        return classNames(baseClass, classes.borderRow4);
      }
    }

    return classes.flexContainer;
  };

  return (
    <div style={{ flex: 1 }}>
      <AutoSizer>
        {({ height, width }) => (
          <Table
            height={height}
            width={width}
            rowHeight={cache.rowHeight}
            headerHeight={24}
            rowCount={tableData.length}
            rowGetter={({ index }) => tableData[index]}
            rowClassName={getRowClassName}
            overscanRowCount={0}
          >
            {columns.map(({ dataKey, label, widthBasis, headerRenderer }) => {
              return (
                <Column
                  key={label}
                  headerRenderer={headerRenderer || defaultHeaderRenderer(sort, handleSort)}
                  headerClassName={classes.tableHead}
                  cellRenderer={cellRenderer}
                  label={label}
                  dataKey={dataKey}
                  width={width * widthBasis}
                />
              );
            })}
          </Table>
        )}
      </AutoSizer>
      {/* alert */}
      <Alert
        open={successAlert}
        id="Alert-aib"
        onClose={() => setSuccessAlert(false)}
        text={i18n.t('DUTYASSIGN_SAVE_SUCCESS_ALERT')}
        type="success"
      />
    </div>
  );
};

const useStyles = makeStyles((theme) => ({
  flexContainer: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    boxSizing: 'border-box',
  },
  borderRow1: {
    borderTop: 'solid 1px #AAAAAA',
    backgroundColor: 'white',
  },
  borderRow2: {
    borderTop: 'solid 1px #AAAAAA',
    backgroundColor: '#fff4cd',
  },
  borderRow3: {
    borderTop: 'solid 1px #AAAAAA',
    backgroundColor: '#feeceb',
  },
  borderRow4: {
    borderTop: 'solid 1px #AAAAAA',
    backgroundColor: 'rgba(240,240,240)',
  },
  borderRow: {
    borderTop: 'solid 1px #AAAAAA',
  },
  tableHead: {
    color: '#538FFF',
    fontFamily: 'NotoSans-Bold',
    fontSize: '0.75rem',
    textTransform: 'uppercase',
    borderBottom: 'none !important',
    alignItems: 'center',
    display: 'flex',
    height: '100%',
  },
  otherInfo: {
    borderTop: 'unset !important',
  },
  headerActionButton: {
    width: '1.5rem',
    height: '1.5rem',
    borderRadius: '50%',
    flexShrink: 0,
  },
  headerActionIcon: {
    fontSize: '1.5rem',
  },
}));

export default TicketTable;
